// Каждый код ошибки договора → что видит игрок и что делает клиент (ux/11 §4.1, E01-T08).
// Тип Record<ErrorCode, …> держит правило «код без строки или строка без кода — красная сборка» в обе стороны:
// новый код в @garages/contract ломает tsc здесь, лишний ключ — тоже (плюс тест по списку ERROR_CODES из zod).
// Тексты — заглушки ux/11 §4.1 и строки ERR-… нарратива (07 §4); окончательная вычитка — E20-T25.
import type { ErrorCode } from '@garages/contract';
import type { ModalId, SystemId } from '../nav/navigator.js';
import { t as tr } from '../i18n/t.js';

/** Ошибки, которые рождаются на клиенте, а не приходят от сервера. */
export type ClientErrorCode = 'NETWORK' | 'TIMEOUT' | 'BAD_RESPONSE';
export type AnyErrorCode = ErrorCode | ClientErrorCode;
type AdminOnlyCode = 'ADMIN_LOGIN_FAILED' | 'TOTP_REQUIRED' | 'LIMIT_EXCEEDED' | 'CSRF_INVALID' | 'OWN_SANCTION' | 'ALREADY_REFUNDED' | 'REFUND_IN_PROGRESS'
  | 'ALREADY_APPEALED' | 'SAME_SIGNER' | 'NOT_VALIDATED' | 'NOT_APPROVED' | 'APPROVAL_STATE' | 'CONFIG_STATE' | 'REASON_REQUIRED' | 'CONFIG_INVALID'
  | 'ACTIVATE_TOO_SOON' | 'REVIEW_REQUIRED';

export type Surface =
  /** клиент сам делает POST /auth/refresh и повторяет запрос */
  | 'refresh'
  /** повторный вход по initData; при неудаче — SCR-BOOT «Открой игру заново» */
  | 'relogin'
  /** системный экран вне стека */
  | 'system'
  /** окно поверх текущего экрана */
  | 'modal'
  /** тост OVL-TOAST поверх экрана */
  | 'toast'
  /** текст у поля или на кнопке действия (ST-NOT-ENOUGH и т. п.) */
  | 'inline'
  /** экран сам обработает по details (STALE_TURN, BATTLE_OVER, STEP_OUT_OF_ORDER) */
  | 'handled'
  /** тихий отчёт без текста игроку */
  | 'silent';

export interface ErrorPolicy {
  surface: Surface;
  /** Текст игроку; {n}, {valuta} и т. п. подставляются из details. */
  text?: string;
  system?: SystemId;
  modal?: ModalId;
  /** Отправить тихий отчёт об ошибке клиента (POST /client-errors). */
  report?: boolean;
  /** Перечитать состояние экрана (кошелёк, список). */
  refetch?: boolean;
}

const notEnough = (what: string): ErrorPolicy => ({ surface: 'inline', text: tr('CL-NET-ERROR-POLICY-01', { p0: what }), refetch: true });
const fallbackInternal: ErrorPolicy = { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-02'), report: true };

export const ERROR_POLICY: Readonly<Record<AnyErrorCode, ErrorPolicy>> = {
  // --- клиентские ---
  NETWORK: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-03') },
  TIMEOUT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-04') },
  BAD_RESPONSE: { ...fallbackInternal },
  // --- 400 ---
  VALIDATION_FAILED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-05'), report: true },
  // --- 401: вход и сессия ---
  AUTH_REQUIRED: { surface: 'refresh' },
  TOKEN_EXPIRED: { surface: 'refresh' },
  INIT_DATA_INVALID: { surface: 'system', system: 'SCR-BOOT', text: tr('CL-NET-ERROR-POLICY-06') },
  INIT_DATA_EXPIRED: { surface: 'system', system: 'SCR-BOOT', text: tr('CL-NET-ERROR-POLICY-06') },
  SIGNATURE_INVALID: { surface: 'relogin', report: true },
  SESSION_ELSEWHERE: { surface: 'modal', modal: 'MOD-SESSION-ELSEWHERE' },
  REFRESH_REUSED: { surface: 'system', system: 'SCR-BOOT', text: tr('CL-NET-ERROR-POLICY-07') },
  // --- 403 ---
  BANNED: { surface: 'system', system: 'SCR-BANNED' },
  FORBIDDEN_ROLE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-08') },
  FEATURE_DISABLED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-09') },
  TALONS_DEBT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-10') },
  PURCHASE_BLOCKED: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-11') },
  TG_LINK_REQUIRED: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-12') },
  CHAT_MUTED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-13') },
  COOP_CLOSED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-14') },
  COOP_FROZEN: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-15') },
  CONSENT_REQUIRED: { surface: 'modal', modal: 'MOD-CONSENT', text: tr('CL-NET-ERROR-POLICY-16') },
  SPEND_LIMIT_REACHED: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-17') },
  FORBIDDEN_ORIGIN: { ...fallbackInternal },
  // --- 404, 409, 410 ---
  NOT_FOUND: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-18'), refetch: true },
  CONFLICT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-19'), refetch: true },
  IDEMPOTENCY_MISMATCH: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-19'), refetch: true, report: true },
  STALE_TURN: { surface: 'handled' },
  BATTLE_ALREADY_ACTIVE: { surface: 'handled', text: tr('CL-NET-ERROR-POLICY-20') },
  ALREADY_IN_COOP: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-21') },
  COOP_FULL: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-22'), refetch: true },
  REQUESTS_LIMIT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-23') },
  SOLD_OUT: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-24') },
  ALREADY_PURCHASED: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-25') },
  BOSS_DEFEATED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-26'), refetch: true },
  RAID_PHASE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-27') },
  STALE_ITEM: { surface: 'handled', refetch: true },
  SHOWCASE_EXPIRED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-28'), refetch: true },
  PRICE_CHANGED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-29'), refetch: true },
  REMOVAL_PENDING: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-30'), refetch: true },
  ELECTION_ACTIVE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-31'), refetch: true },
  ALREADY_VOTED: { surface: 'handled', refetch: true },
  ALREADY_SUBMITTED: { surface: 'handled', refetch: true },
  COMBO_TAKEN: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-32') },
  WISH_STATE: { surface: 'handled', refetch: true },
  IN_BATTLE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-33') },
  BATTLE_OVER: { surface: 'handled' },
  STEP_OUT_OF_ORDER: { surface: 'handled', report: true },
  ALREADY_PAID: { surface: 'handled' },
  // Гараж и машина (tech/03 §5, Р-51 бэкенда) — тексты ux/11 §4.1 «Гараж и машина».
  NOT_UPGRADING: { surface: 'handled', refetch: true },
  CANCEL_WINDOW_PASSED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-CANCEL-WINDOW'), refetch: true },
  TRIP_RUNNING: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-TRIP-RUNNING'), refetch: true },
  NOT_RUNNING: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-NOT-RUNNING'), refetch: true },
  NOT_FINISHED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-NOT-FINISHED'), refetch: true },
  ALREADY_CLAIMED: { surface: 'handled', refetch: true },
  GONE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-34'), refetch: true },
  // --- 422: ресурсы и правила ---
  NOT_ENOUGH_ENERGY: { surface: 'modal', modal: 'MOD-ENERGY' },
  NOT_ENOUGH_NUTS: notEnough(tr('CL-NET-ERROR-POLICY-35')),
  NOT_ENOUGH_DIESEL: notEnough(tr('CL-NET-ERROR-POLICY-36')),
  NOT_ENOUGH_TALONS: notEnough(tr('CL-NET-ERROR-POLICY-37')),
  NOT_ENOUGH_KEYS: notEnough(tr('CL-NET-ERROR-POLICY-38')),
  NOT_ENOUGH_TICKETS: notEnough(tr('CL-NET-ERROR-POLICY-39')),
  NOT_ENOUGH_SCRAP: notEnough(tr('CL-NET-ERROR-POLICY-40')),
  NOT_ENOUGH_MASTER_XP: notEnough(tr('CL-NET-ERROR-POLICY-41')),
  NOT_ENOUGH_S8_TOKENS: notEnough(tr('CL-NET-ERROR-POLICY-42')),
  NOT_ENOUGH_MARKS: notEnough(tr('CL-NET-ERROR-POLICY-43')),
  NOT_ENOUGH_EVENT_TOKENS: notEnough(tr('CL-NET-ERROR-POLICY-44')),
  LEVEL_TOO_LOW: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-45') },
  BOSS_LOCKED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-46') },
  COOLDOWN: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-47') },
  DAILY_LIMIT: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-48') },
  WEEKLY_LIMIT: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-49') },
  LIMIT_REACHED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-50') },
  RAID_CAP: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-51') },
  CHAT_REJECTED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-52') },
  REQUIREMENT_NOT_MET: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-53') },
  TRANSFER_NOT_ALLOWED: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-54') },
  MASTERY_NOT_AVAILABLE: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-55') },
  ALREADY_FULL: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-56'), refetch: true },
  LIMIT_INVALID: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-57') },
  TEXT_REJECTED: { surface: 'inline', text: tr('ASM-WISH-TEXT-BAD') },
  NOT_ENOUGH_POINTS: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-58'), refetch: true },
  VERSION_OUTDATED: { surface: 'handled', refetch: true },
  ITEM_LOCKED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-59') },
  ITEM_EQUIPPED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-60') },
  MAX_LEVEL: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-61') },
  MODE_NOT_ALLOWED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-62'), refetch: true },
  CAR_NOT_READY: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-CAR-NOT-READY'), refetch: true },
  NOT_IN_COOP: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-63') },
  // Кооперативы (tech/03 §7, Р-54 бэкенда) — черновые CL-… на вычитку нарратива.
  NAME_TAKEN: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-NAME-TAKEN') },
  NAME_INVALID: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-NAME-INVALID') },
  REJOIN_COOLDOWN: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-REJOIN-COOLDOWN'), refetch: true },
  CHAIRMAN_MUST_TRANSFER: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-CHAIRMAN-MUST-TRANSFER') },
  NOT_SCHEDULED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-NOT-SCHEDULED'), refetch: true },
  DISBAND_SCHEDULED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-DISBAND-SCHEDULED'), refetch: true },
  INSTANCE_ACTIVE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-INSTANCE-ACTIVE'), refetch: true },
  NO_SUMMONS: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-NO-SUMMONS'), refetch: true },
  BOSS_NOT_ELIGIBLE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-BOSS-NOT-ELIGIBLE') },
  NO_PLANS: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-NO-PLANS'), refetch: true },
  COOP_LEVEL_TOO_LOW: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-COOP-LEVEL-TOO-LOW') },
  BUILDING_MAX: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-BUILDING-MAX'), refetch: true },
  STORAGE_FULL: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-STORAGE-FULL') },
  TENURE_TOO_SHORT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-COOP-TENURE-TOO-SHORT') },
  // Социальное (tech/03 §3, §15; Р-59 бэкенда) — черновые CL-… на вычитку.
  ALREADY_RESPECTED_TODAY: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-RESPECTED-TODAY'), refetch: true },
  SELF: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-SELF') },
  FRIENDS_LIMIT: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-FRIENDS-LIMIT') },
  // Чат, задания, группа Telegram (tech/03 §7–§9, Р-68 бэкенда) — черновые CL-… на вычитку.
  ALREADY_REPORTED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-ALREADY-REPORTED') },
  // Удаление аккаунта (tech/03 §3, Р-90 бэкенда) — черновые CL-… на вычитку.
  // Аналитика (tech/03 §17, Р-103): пачку клиент делит пополам, игроку не показывает.
  PAYLOAD_TOO_LARGE: { surface: 'handled', text: tr('CL-NET-ERROR-POLICY-PAYLOAD-TOO-LARGE') },
  HAS_PENDING_PAYMENTS: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-HAS-PENDING-PAYMENTS') },
  DELETE_ALREADY_SCHEDULED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-DELETE-ALREADY-SCHEDULED'), refetch: true },
  BODY_INVALID: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-BODY-INVALID') },
  NOT_COMPLETED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-NOT-COMPLETED'), refetch: true },
  URL_INVALID: { surface: 'inline', text: tr('CL-NET-ERROR-POLICY-URL-INVALID') },
  // Коды Admin API (tech/03 §22, E17): игровой клиент их не получает — общий запасной ответ с тихим отчётом.
  ...Object.fromEntries((['ADMIN_LOGIN_FAILED', 'TOTP_REQUIRED', 'LIMIT_EXCEEDED', 'CSRF_INVALID', 'OWN_SANCTION', 'ALREADY_REFUNDED', 'REFUND_IN_PROGRESS',
    'ALREADY_APPEALED', 'SAME_SIGNER', 'NOT_VALIDATED', 'NOT_APPROVED', 'APPROVAL_STATE', 'CONFIG_STATE', 'REASON_REQUIRED', 'CONFIG_INVALID',
    'ACTIVATE_TOO_SOON', 'REVIEW_REQUIRED'] as const).map((c) => [c, { ...fallbackInternal }])) as Record<AdminOnlyCode, ErrorPolicy>,
  // --- версия, частота, сервер ---
  CLIENT_TOO_OLD: { surface: 'system', system: 'SCR-UPDATE-REQUIRED' },
  RATE_LIMITED: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-64') },
  INTERNAL: { ...fallbackInternal, report: false },
  MAINTENANCE: { surface: 'system', system: 'SCR-MAINTENANCE' },
  UNAVAILABLE: { surface: 'toast', text: tr('CL-NET-ERROR-POLICY-65') },
  INVOICE_UNAVAILABLE: { surface: 'modal', modal: 'MOD-PAY-ERROR', text: tr('CL-NET-ERROR-POLICY-66') },
};

/** Текст REQUIREMENT_NOT_MET по `details.reason` сервера (ux/11 §4.1: «текст требования из details»). */
export const REQUIREMENT_REASONS: Record<string, string> = {
  tutorial: tr('CL-NET-ERROR-POLICY-67'),
  tutorial_step: tr('CL-NET-ERROR-POLICY-67'),
  tutorial_locked: tr('CL-NET-ERROR-POLICY-68'),
  stat_cap: tr('CL-NET-ERROR-POLICY-69'),
};

/** Неизвестный код (сервер новее клиента) — как INTERNAL плюс отчёт (ux/11 §4.1, последняя строка). */
export function policyFor(code: string): ErrorPolicy {
  return (ERROR_POLICY as Record<string, ErrorPolicy>)[code] ?? fallbackInternal;
}
